import React, { useState, useRef, useEffect } from 'react';
import {
  getDocumentAsBase64,
  downloadDocxFile,
  getContentControlTags,
  getDocumentProperties,
  categorizeTags,
  loadDocumentFromBase64,
  setDocumentProperty,
} from '../services/wordHelper';
import { getAuthHeaders, API_URL as API_BASE } from '../services/auth';
import './ImportExport.css';

interface ValidationTemplate {
  name: string;
  hasConflict: boolean;
  existingId?: number;
  placeholderCount: number;
  ruleGroupCount: number;
  keywordCount: number;
}

interface ValidationResult {
  valid: boolean;
  templates: ValidationTemplate[];
  missingContextFields: string[];
  errors: string[];
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
  pendingCreate?: number;
}

/**
 * Shape of the prefill the .dpt importer hands off to the parent when a
 * single-template import resolves to "new template — needs user action".
 * The parent (App.tsx) uses it to switch to the SaveTemplate view in
 * save-as-new mode, with name + DocumentType prefilled and the source
 * PublicId carried forward so the eventual SAVE preserves cross-env identity.
 */
export interface PendingCreateImport {
  fileData: string;
  suggestedName: string;
  suggestedDocumentType: string;
  sourcePublicId: string | null;
}

/**
 * Binding handed to the parent when an import COMMITS a version (Branch A
 * version-append, or Branch B 'replace'). The parent uses it to set the
 * App-level activeTemplate so the Template section + BindingBanner reflect
 * the just-imported template — without this the import loads the docx but
 * the taskpane still shows "no template saved".
 */
export interface CommittedImport {
  id: number;
  name: string;
  versionNumber: number;
  versionStatus: 'Draft' | 'PendingApproval' | 'Published' | 'Superseded' | 'Rejected';
  isLocked: boolean;
  publicId?: string;
}

interface ImportExportProps {
  /**
   * Fired when the import resolves to Branch C (new template) — the import
   * server has NOT committed a Templates row. Parent should navigate the
   * pane to the Save view in save-as-new mode and let the user pick the
   * Document Type before clicking Save.
   */
  onPendingCreateImport?: (prefill: PendingCreateImport) => void;
  /**
   * Fired when the import COMMITTED a version onto an existing template
   * (version-append / replace). Parent should adopt this as the active
   * template so the Template section and binding banner update.
   */
  onImportCommitted?: (template: CommittedImport) => void;
}

export function ImportExport({ onPendingCreateImport, onImportCommitted }: ImportExportProps = {}) {
  const [activeTab, setActiveTab] = useState<'export' | 'import'>('export');
  const [exporting, setExporting] = useState(false);
  const [exportingPackage, setExportingPackage] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmImport, setConfirmImport] = useState<string | null>(null);
  const [importFilename, setImportFilename] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Package import state
  const [packageFile, setPackageFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [conflicts, setConflicts] = useState<Record<string, 'skip' | 'create' | 'replace'>>({});
  const [validating, setValidating] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const packageFileInputRef = useRef<HTMLInputElement>(null);

  // ── Package export state ────────────────────────────────────────────────
  // Held in React state (rather than the previous DOM-read pattern) so we can
  // react to ID changes — fetch the template's version list as soon as a
  // valid id is entered, and render a version picker driven by that list.
  const [exportTemplateId, setExportTemplateId] = useState<string>('');
  const [exportVersions, setExportVersions] = useState<
    Array<{ versionNumber: number; versionStatus: string; createdAt?: string }>
  >([]);
  // Empty string ⇒ "Latest (default)"; numeric string ⇒ explicit version pick.
  // Stored as string for clean two-way binding with the <select>.
  const [exportSelectedVersion, setExportSelectedVersion] = useState<string>('');
  const [exportVersionsLoading, setExportVersionsLoading] = useState(false);
  const [exportVersionsError, setExportVersionsError] = useState<string | null>(null);

  // Fetch versions whenever the user types a valid template ID. Lightly
  // debounced so we don't fire a request for every keystroke while the user
  // is mid-typing (e.g. "1" → "10" → "108" → "1080" would otherwise hit the
  // server four times). 250 ms is enough to feel instant when the user
  // pastes a full id; users typing digit-by-digit will see one fetch fire
  // after the last keypress.
  useEffect(() => {
    const idNum = parseInt(exportTemplateId, 10);
    if (!idNum || idNum < 1) {
      setExportVersions([]);
      setExportSelectedVersion('');
      setExportVersionsError(null);
      return;
    }
    setExportVersionsLoading(true);
    setExportVersionsError(null);
    const handle = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_BASE}/templates/${idNum}/versions`, {
          headers: { ...getAuthHeaders() },
        });
        if (!resp.ok) {
          // Distinguish "no such template" (404) from server errors so the
          // user sees an actionable message rather than a generic failure.
          if (resp.status === 404) {
            throw new Error(`Template ${idNum} not found.`);
          }
          throw new Error(`Could not load versions (HTTP ${resp.status}).`);
        }
        const json = await resp.json();
        const versions: Array<any> = json?.data || [];
        // Filter soft-deleted versions and sort newest-first so the dropdown
        // matches the user's mental model (most recent at top).
        const live = versions
          .filter((v) => v.VersionStatus !== 'Deleted')
          .sort((a, b) => b.VersionNumber - a.VersionNumber)
          .map((v) => ({
            versionNumber: v.VersionNumber,
            versionStatus: v.VersionStatus,
            createdAt: v.CreatedAt || v.UploadedAt,
          }));
        setExportVersions(live);
        setExportSelectedVersion(''); // reset to "Latest" on each id change
      } catch (err: any) {
        setExportVersionsError(err?.message || 'Could not load versions');
        setExportVersions([]);
      } finally {
        setExportVersionsLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [exportTemplateId]);

  // --- DOCX Export (existing functionality) ---
  const handleExport = async () => {
    setExporting(true);
    setMessage(null);
    try {
      const base64 = await getDocumentAsBase64();

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-export.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const tags = await getContentControlTags();
      const uniqueTags = [...new Set(tags.map(t => t.tag))];
      const categorized = categorizeTags(uniqueTags);
      const properties = await getDocumentProperties();

      const metadata = {
        exportedAt: new Date().toISOString(),
        properties,
        contentControls: {
          total: tags.length,
          uniqueTags: uniqueTags.length,
          simple: categorized.simple,
          conditional: categorized.conditional,
          repeat: categorized.repeat,
        },
        tags: tags.map(t => ({
          tag: t.tag,
          title: t.title,
          type: t.type,
        })),
      };

      const metaBlob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
      const metaUrl = URL.createObjectURL(metaBlob);
      const metaLink = document.createElement('a');
      metaLink.href = metaUrl;
      metaLink.download = 'template-export-metadata.json';
      document.body.appendChild(metaLink);
      metaLink.click();
      document.body.removeChild(metaLink);
      URL.revokeObjectURL(metaUrl);

      setMessage({ type: 'success', text: 'Template and metadata exported successfully.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Export failed.',
      });
    } finally {
      setExporting(false);
    }
  };

  // --- Package Export (.dpt) ---
  // `versionNumber` is optional. When omitted (or null) the server falls back
  // to "latest" — the legacy default — so this signature stays compatible
  // with anything else that might call this function in the future.
  const handlePackageExport = async (templateId: number, versionNumber: number | null) => {
    setExportingPackage(true);
    setMessage(null);
    try {
      const body: Record<string, unknown> = {
        templateIds: [templateId],
        includeRules: true,
        includeDraft: true,
      };
      if (versionNumber != null) {
        body.versionSelections = { [String(templateId)]: versionNumber };
      }
      const response = await fetch(`${API_BASE}/templates/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error?.message || `Export failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Include the version in the filename so the user can tell at a glance
      // which version a downloaded .dpt represents — important when promoting
      // between envs where the file lingers on disk awhile before re-import.
      const versionSuffix = versionNumber ? `-v${versionNumber}` : '-latest';
      a.download = `template-${templateId}${versionSuffix}-export.dpt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({
        type: 'success',
        text: versionNumber
          ? `Template package exported (v${versionNumber}).`
          : 'Template package exported (latest version).',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Package export failed.',
      });
    } finally {
      setExportingPackage(false);
    }
  };

  // --- DOCX Import (existing functionality) ---
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      setMessage({ type: 'error', text: 'Please select a .docx file.' });
      return;
    }

    setImportFilename(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      setConfirmImport(base64);
    };
    reader.onerror = () => {
      setMessage({ type: 'error', text: 'Failed to read the selected file.' });
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmImport = async () => {
    if (!confirmImport) return;
    setImporting(true);
    setMessage(null);
    try {
      // Real import: POST to /templates/import-docx so the file becomes a new
      // Draft template in the templates list (the historical behaviour was
      // just to load the file into the current Word doc via Office.js — not
      // an import at all).
      const fname = importFilename || 'imported-template.docx';
      const name  = fname.replace(/\.docx$/i, '').trim() || 'Imported Template';
      const response = await fetch(`${API_BASE}/templates/import-docx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name, fileData: confirmImport }),
      });
      const body = await response.json().catch(() => ({} as any));
      if (!response.ok || !body?.success) {
        throw new Error(body?.error?.message || `Import failed (HTTP ${response.status})`);
      }
      const data = body.data || {};
      const renamedNote = data.renamed
        ? ` (renamed to "${data.name}" — a template with the original name already exists)`
        : '';
      setMessage({
        type: 'success',
        text: `Imported "${name}" as Draft v${data.versionNumber ?? 1}${renamedNote}. Open it from the Templates list.`,
      });
      setConfirmImport(null);
      setImportFilename('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Import failed.',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setConfirmImport(null);
    setImportFilename('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // --- Package Import (.dpt) ---
  const handlePackageFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.dpt') && !file.name.endsWith('.zip')) {
      setMessage({ type: 'error', text: 'Please select a .dpt or .zip file.' });
      return;
    }

    setPackageFile(file);
    setValidationResult(null);
    setImportResult(null);
    setConflicts({});
    setMessage(null);

    // Validate the package — read .dpt as JSON and send as application/json
    setValidating(true);
    try {
      const fileText = await file.text();
      let pkg: any;
      try { pkg = JSON.parse(fileText); } catch { throw new Error('Invalid .dpt file — not valid JSON'); }

      const response = await fetch(`${API_BASE}/templates/import/validate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(pkg),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error?.message || `Validation failed (${response.status})`);
      }

      const result = await response.json();
      const validation = result.data as ValidationResult;
      setValidationResult(validation);

      // Set default conflict resolution for templates with conflicts
      const defaultConflicts: Record<string, 'skip' | 'create' | 'replace'> = {};
      for (const tmpl of validation.templates) {
        if (tmpl.hasConflict) {
          defaultConflicts[tmpl.name] = 'create';
        }
      }
      setConflicts(defaultConflicts);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Validation failed.',
      });
    } finally {
      setValidating(false);
    }
  };

  const handlePackageImport = async () => {
    if (!packageFile) return;
    setImporting(true);
    setMessage(null);
    setImportResult(null);

    try {
      const fileText = await packageFile.text();
      let pkg: any;
      try { pkg = JSON.parse(fileText); } catch { throw new Error('Invalid .dpt file'); }
      pkg.conflicts = conflicts;
      // Ask the server to echo back the docx blob in each result so we can
      // auto-load the imported template into Word — addresses the BA gap
      // "after import, the document should open". Only single-template
      // .dpt packages are supported in this UI, so we always want this.
      pkg.autoLoad = true;

      const response = await fetch(`${API_BASE}/templates/import`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(pkg),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error?.message || `Import failed (${response.status})`);
      }

      const result = await response.json();
      const summary = result.data as ImportResult & { results?: any[] };
      setImportResult(summary);

      // Single-template UI — pick the first result to decide what to do.
      // Bulk handling (multiple results, partial failures) would require a
      // listing UI that the current Import panel doesn't have; surface the
      // count + errors but only act on results[0].
      const first = summary.results?.[0];
      if (first?.action === 'pending_create') {
        // No DB write happened. Hand off to the parent: it will load the
        // docx into Word, stamp dp_publicId + dp_docType, then navigate to
        // the Save Template view in save-as-new mode so the user picks
        // (or confirms) the DocumentType before clicking Save. Don't
        // touch Word.js from here — the parent owns view+state.
        if (onPendingCreateImport && first.fileData) {
          onPendingCreateImport({
            fileData: first.fileData,
            suggestedName: first.suggestedName || first.name || 'Imported Template',
            suggestedDocumentType: first.suggestedDocumentType || 'General',
            sourcePublicId: first.sourcePublicId || null,
          });
          // Parent will navigate; clear our local state but skip the
          // generic success banner (the parent flow takes over).
          setMessage(null);
        } else {
          // No callback wired or no file in response — degrade gracefully
          // with a clear message instead of a silent no-op.
          setMessage({
            type: 'error',
            text: 'Import returned a new-template result but the host couldn\'t open it. Refresh and try again, or save it via the Templates list.',
          });
        }
      } else if (first?.action === 'version_appended' || first?.action === 'replaced') {
        // Committed branch — DB row exists. Load the docx into Word and
        // stamp the full dp_* binding so the BindingBanner instantly
        // recognises it as `match` and the user can keep editing.
        try {
          if (first.fileData) {
            await loadDocumentFromBase64(first.fileData);
            await setDocumentProperty('dp_templateId', String(first.templateId));
            await setDocumentProperty('dp_templateName', first.name || '');
            await setDocumentProperty('dp_versionNumber', String(first.versionNumber ?? 1));
            await setDocumentProperty('dp_versionStatus', String(first.versionStatus || 'Draft'));
            if (first.publicId) await setDocumentProperty('dp_publicId', String(first.publicId));
            if (first.documentType) {
              await setDocumentProperty('dp_docType', String(first.documentType));
              await setDocumentProperty('dp_docTypeGroup', String(first.documentType));
            }
          }
        } catch (loadErr: any) {
          // Loading is a UX nicety; the import itself succeeded.
          // Surface the load failure but don't pretend the import did.
          // eslint-disable-next-line no-console
          console.warn('[ImportExport] auto-load after import failed:', loadErr);
        }
        // Tell the parent to adopt this as the active template. Without
        // this, the docx loads but App.activeTemplate stays null, so the
        // Template section shows "no template saved" even though a version
        // was just committed. The parent's persistTemplate also re-stamps
        // dp_* (idempotent) and flips the binding banner to "match".
        if (onImportCommitted && first.templateId) {
          onImportCommitted({
            id: first.templateId,
            name: first.name || 'Imported Template',
            versionNumber: first.versionNumber ?? 1,
            versionStatus: (first.versionStatus as CommittedImport['versionStatus']) || 'Draft',
            isLocked: false,
            publicId: first.publicId || undefined,
          });
        }
        const verb = first.action === 'version_appended' ? 'appended' : 'replaced';
        setMessage({
          type: 'success',
          text: `Import complete — version ${verb}. Template "${first.name}" v${first.versionNumber} opened for editing.`,
        });
      } else if (summary.errors.length === 0) {
        setMessage({
          type: 'success',
          text: `Import complete: ${summary.imported} imported, ${summary.skipped} skipped.`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `Import finished with errors: ${summary.imported} imported, ${summary.skipped} skipped, ${summary.errors.length} errors.`,
        });
      }

      // Reset
      setPackageFile(null);
      setValidationResult(null);
      setConflicts({});
      if (packageFileInputRef.current) {
        packageFileInputRef.current.value = '';
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Import failed.',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCancelPackageImport = () => {
    setPackageFile(null);
    setValidationResult(null);
    setImportResult(null);
    setConflicts({});
    if (packageFileInputRef.current) {
      packageFileInputRef.current.value = '';
    }
  };

  return (
    <div className="dp-import-export">
      {/* Tab Selector */}
      <div className="dp-import-export__tabs">
        <button
          className={`dp-import-export__tab ${activeTab === 'export' ? 'dp-import-export__tab--active' : ''}`}
          onClick={() => { setActiveTab('export'); setMessage(null); }}
        >
          Export
        </button>
        <button
          className={`dp-import-export__tab ${activeTab === 'import' ? 'dp-import-export__tab--active' : ''}`}
          onClick={() => { setActiveTab('import'); setMessage(null); }}
        >
          Import
        </button>
      </div>

      {activeTab === 'export' && (
        <>
          {/* DOCX Export */}
          <div className="dp-import-export__section">
            <h3 className="dp-import-export__section-title">Export as DOCX</h3>
            <p className="dp-import-export__desc">
              Export the current document as a .docx file along with a JSON metadata file containing
              document properties and content control tags.
            </p>
            <button
              className="dp-import-export__btn dp-import-export__btn--primary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export Template (DOCX)'}
            </button>
          </div>

          <hr className="dp-import-export__divider" />

          {/* Package Export */}
          <div className="dp-import-export__section">
            <h3 className="dp-import-export__section-title">Export as Package (.dpt)</h3>
            <p className="dp-import-export__desc">
              Export a template with all metadata (rules, placeholders, keywords, conditions) as a
              portable .dpt package for moving between environments.
            </p>
            <div className="dp-import-export__package-export">
              <label className="dp-import-export__desc">
                Template ID:
                <input
                  type="number"
                  className="dp-import-export__text-input"
                  placeholder="Enter template ID"
                  min={1}
                  value={exportTemplateId}
                  onChange={(e) => setExportTemplateId(e.target.value)}
                />
              </label>

              {/* Version picker — appears as soon as a valid id resolves to
                  a versions list. "Latest" stays the default so the existing
                  one-click flow still works; users who need a specific
                  version pick from the dropdown. The selected status badge
                  is right there so a draft-vs-published mix-up is harder. */}
              <label className="dp-import-export__desc">
                Version:
                {exportVersionsLoading ? (
                  <span className="dp-import-export__hint">Loading versions…</span>
                ) : exportVersionsError ? (
                  <span className="dp-import-export__hint dp-import-export__hint--error">
                    {exportVersionsError}
                  </span>
                ) : exportVersions.length > 0 ? (
                  <select
                    className="dp-import-export__text-input"
                    value={exportSelectedVersion}
                    onChange={(e) => setExportSelectedVersion(e.target.value)}
                    disabled={exportingPackage}
                  >
                    <option value="">
                      Latest (v{exportVersions[0].versionNumber} {exportVersions[0].versionStatus})
                    </option>
                    {exportVersions.map((v) => (
                      <option key={v.versionNumber} value={String(v.versionNumber)}>
                        v{v.versionNumber} — {v.versionStatus}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="dp-import-export__hint">Enter a template ID above to load versions.</span>
                )}
              </label>

              <button
                className="dp-import-export__btn dp-import-export__btn--primary"
                onClick={() => {
                  const id = parseInt(exportTemplateId, 10);
                  if (!id || id < 1) {
                    setMessage({ type: 'error', text: 'Please enter a valid template ID.' });
                    return;
                  }
                  const versionNumber = exportSelectedVersion
                    ? parseInt(exportSelectedVersion, 10)
                    : null;
                  handlePackageExport(id, versionNumber);
                }}
                disabled={exportingPackage || !exportTemplateId || exportVersionsLoading}
              >
                {exportingPackage ? 'Exporting...' : 'Export Package (.dpt)'}
              </button>
            </div>
          </div>
        </>
      )}

      {activeTab === 'import' && (
        <>
          {/* DOCX Import */}
          <div className="dp-import-export__section">
            <h3 className="dp-import-export__section-title">Import DOCX</h3>
            <p className="dp-import-export__desc">
              Upload a .docx file to replace the current document content.
            </p>

            <label className="dp-import-export__file-label">
              <span className="dp-import-export__file-btn">Choose .docx File</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={handleFileSelect}
                className="dp-import-export__file-input"
              />
            </label>

            {confirmImport && (
              <div className="dp-import-export__confirm">
                <h4>Replace Document Content?</h4>
                <p>
                  Importing <strong>{importFilename}</strong> will replace all content in the current
                  document. This action cannot be undone.
                </p>
                <div className="dp-import-export__confirm-actions">
                  <button
                    className="dp-import-export__btn dp-import-export__btn--danger"
                    onClick={handleConfirmImport}
                    disabled={importing}
                  >
                    {importing ? 'Importing...' : 'Replace Document'}
                  </button>
                  <button
                    className="dp-import-export__btn dp-import-export__btn--cancel"
                    onClick={handleCancelImport}
                    disabled={importing}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <hr className="dp-import-export__divider" />

          {/* Package Import (.dpt) */}
          <div className="dp-import-export__section">
            <h3 className="dp-import-export__section-title">Import Package (.dpt)</h3>
            <p className="dp-import-export__desc">
              Upload a .dpt template package to import templates with all metadata into the system.
            </p>

            <label className="dp-import-export__file-label">
              <span className="dp-import-export__file-btn">Choose .dpt File</span>
              <input
                ref={packageFileInputRef}
                type="file"
                accept=".dpt,.zip"
                onChange={handlePackageFileSelect}
                className="dp-import-export__file-input"
              />
            </label>

            {validating && (
              <div className="dp-import-export__validating">Validating package...</div>
            )}

            {validationResult && (
              <div className="dp-import-export__validation">
                <h4>Package Contents</h4>

                {validationResult.errors.length > 0 && (
                  <div className="dp-import-export__message dp-import-export__message--error">
                    {validationResult.errors.map((e, i) => (
                      <div key={i}>{e}</div>
                    ))}
                  </div>
                )}

                <div className="dp-import-export__template-list">
                  {validationResult.templates.map((tmpl, idx) => (
                    <div key={idx} className="dp-import-export__template-item">
                      <div className="dp-import-export__template-name">
                        <strong>{tmpl.name}</strong>
                        {tmpl.hasConflict && (
                          <span className="dp-import-export__conflict-badge">Name Conflict</span>
                        )}
                      </div>
                      <div className="dp-import-export__template-meta">
                        {tmpl.placeholderCount} placeholders, {tmpl.ruleGroupCount} rule groups, {tmpl.keywordCount} keywords
                      </div>
                      {tmpl.hasConflict && (
                        <select
                          className="dp-import-export__conflict-select"
                          value={conflicts[tmpl.name] || 'create'}
                          onChange={(e) =>
                            setConflicts((prev) => ({
                              ...prev,
                              [tmpl.name]: e.target.value as 'skip' | 'create' | 'replace',
                            }))
                          }
                        >
                          <option value="create">Create New (with suffix)</option>
                          <option value="replace">Replace Existing</option>
                          <option value="skip">Skip</option>
                        </select>
                      )}
                    </div>
                  ))}
                </div>

                {validationResult.missingContextFields.length > 0 && (
                  <div className="dp-import-export__missing-fields">
                    <strong>Missing context fields (will be auto-created):</strong>
                    <ul>
                      {validationResult.missingContextFields.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="dp-import-export__confirm-actions">
                  <button
                    className="dp-import-export__btn dp-import-export__btn--primary"
                    onClick={handlePackageImport}
                    disabled={importing || validationResult.templates.length === 0}
                  >
                    {importing ? 'Importing...' : `Import ${validationResult.templates.length} Template(s)`}
                  </button>
                  <button
                    className="dp-import-export__btn dp-import-export__btn--cancel"
                    onClick={handleCancelPackageImport}
                    disabled={importing}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {importResult && (
              <div className="dp-import-export__import-result">
                <h4>Import Results</h4>
                <p>{importResult.imported} imported, {importResult.skipped} skipped</p>
                {importResult.errors.length > 0 && (
                  <ul className="dp-import-export__error-list">
                    {importResult.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Message */}
      {message && (
        <div className={`dp-import-export__message dp-import-export__message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
}
